import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import CallerIdParser from "./CallerIdParser";
import SampleEmailContent from "./SampleEmailContent";
import SecuritySettings from "./SecuritySettings";
import PageHeader from "../../../shared/components/common/PageHeader";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isViewer } from "../../../shared/utils/roles";
import {
  deletePbxWhitelistedIpThunk,
  loadPbxConfigThunk,
  parsePbxEmailThunk,
  savePbxConfigThunk,
  selectPbxCanSaveChanges,
  selectPbxCallerIdTag,
  selectPbxEnforceTLS,
  selectPbxIsParsing,
  selectPbxLeftEmail,
  selectPbxNewIpAddress,
  selectPbxParseError,
  selectPbxRightEmail,
  selectPbxWhitelistIps,
  setCallerIdTag,
  setEnforceTLS,
  setIpWhitelistRecords,
  setLeftEmail,
  setNewIpAddress,
  setRightEmail,
} from "../store/pbxConfigSlice";

const sanitizeIpList = (ips) =>
  Array.from(
    new Set(
      (Array.isArray(ips) ? ips : [])
        .map((ip) => String(ip || "").trim())
        .filter(Boolean),
    ),
  );

const PBXConfig = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const resolvedClientId = useMemo(
    () => Number(currentUser?.client_id || 0) || null,
    [currentUser?.client_id],
  );

  const callerIdTag = useSelector(selectPbxCallerIdTag);
  const leftEmail = useSelector(selectPbxLeftEmail);
  const rightEmail = useSelector(selectPbxRightEmail);
  const enforceTLS = useSelector(selectPbxEnforceTLS);
  const ipWhitelist = useSelector(selectPbxWhitelistIps);
  const newIpAddress = useSelector(selectPbxNewIpAddress);
  const canSaveChanges = useSelector(selectPbxCanSaveChanges);
  const isParsing = useSelector(selectPbxIsParsing);
  const parseError = useSelector(selectPbxParseError);

  const viewer = isViewer(currentUser?.user_role);

  const handleTestParsing = useCallback(() => {
    if (!resolvedClientId) {
      return;
    }
    dispatch(
      parsePbxEmailThunk({
        clientId: resolvedClientId,
        emailBody: leftEmail,
        delimiter: callerIdTag,
      }),
    );
  }, [dispatch, resolvedClientId, leftEmail, callerIdTag]);

  const setIpWhitelist = useCallback(
    (nextIpListOrUpdater) => {
      const nextIpList =
        typeof nextIpListOrUpdater === "function"
          ? nextIpListOrUpdater(ipWhitelist)
          : nextIpListOrUpdater;

      dispatch(setIpWhitelistRecords(sanitizeIpList(nextIpList)));
    },
    [dispatch, ipWhitelist],
  );

  useEffect(() => {
    if (!resolvedClientId) {
      return;
    }
    dispatch(loadPbxConfigThunk(resolvedClientId));
  }, [dispatch, resolvedClientId]);

  const handleSaveConfiguration = useCallback(async () => {
    if (!resolvedClientId) {
      return {
        success: false,
        message: "Unable to save PBX configuration.",
      };
    }
    const action = await dispatch(savePbxConfigThunk(resolvedClientId));

    if (savePbxConfigThunk.fulfilled.match(action)) {
      return {
        success: true,
        message: "PBX configuration saved successfully.",
      };
    }

    return {
      success: false,
      message: action.payload || "Unable to save PBX configuration.",
    };
  }, [dispatch, resolvedClientId]);

  const handleDeleteWhitelistIp = useCallback(
    (ipAddress) => {
      dispatch(
        deletePbxWhitelistedIpThunk({
          clientId: resolvedClientId,
          ipAddress,
        }),
      );
    },
    [dispatch, resolvedClientId],
  );

  return (
    <div className="w-full h-full bg-transparent">
      <PageHeader
        title="PBX Configuration"
        subtitle="Configure PBX system integration settings"
      />

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-5 px-2 sm:px-3">
        {/* Left Column */}
        <div className="flex-1 lg:flex-[1.2]">
          <CallerIdParser
            callerIdTag={callerIdTag}
            setCallerIdTag={(value) => dispatch(setCallerIdTag(value))}
            canViewSettings={!viewer}
          />
        </div>

        {/* Right Column  */}
        <div className="flex-1">
          <SampleEmailContent
            leftEmail={leftEmail}
            rightEmail={rightEmail}
            setLeftEmail={(value) => dispatch(setLeftEmail(value))}
            setRightEmail={(value) => dispatch(setRightEmail(value))}
            onTestParsing={handleTestParsing}
            isParsing={isParsing}
            parseError={parseError}
          />
        </div>
      </div>
        <div className="mt-4 sm:mt-5 px-2 sm:px-3">
          <SecuritySettings
            enforceTLS={enforceTLS}
            setEnforceTLS={(value) => dispatch(setEnforceTLS(value))}
            ipWhitelist={ipWhitelist}
            setIpWhitelist={setIpWhitelist}
            onDeleteIpAddress={handleDeleteWhitelistIp}
            newIpAddress={newIpAddress}
            setNewIpAddress={(value) => dispatch(setNewIpAddress(value))}
            onSaveConfiguration={handleSaveConfiguration}
            canSaveChanges={canSaveChanges}
            canViewSettings={!viewer}
          />
        </div>
    </div>
  );
};

export default PBXConfig;
